import {
  MemberTransferReasonDto,
  MemberTransferRequestFormType,
} from '@act/shared/data-transfer-objects';
import { SelectOption } from '@act/storybook';
import { Control, FieldErrors } from 'react-hook-form';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import ControlledGuideSelector from './components/ControlledGuideSelector';

interface TransferRequestFormConfigParams {
  memberTransferReasons: MemberTransferReasonDto[];
  selectedTransferReason: MemberTransferReasonDto | null;
  addressOptions: SelectOption<number>[] | undefined;
  control: Control<MemberTransferRequestFormType>;
  errors: FieldErrors<MemberTransferRequestFormType>;
}

export const createTransferRequestFormConfig = ({
  memberTransferReasons,
  selectedTransferReason,
  addressOptions,
  control,
  errors,
}: TransferRequestFormConfigParams): FormConfig<MemberTransferRequestFormType> => {
  const formConfig: FormConfig<MemberTransferRequestFormType> = [
    {
      type: 'label',
      label: 'What is the reason for the transfer?',
    },
    {
      type: 'input',
      name: 'transferReasonId',
      input: 'radioGroup',
      label: '',
      props: {
        options: memberTransferReasons.map(reason => ({
          label: reason.text,
          value: reason.id,
        })),
        trackingName: 'transfer-reason-radio-group',
      },
    },
  ];

  if (selectedTransferReason?.detailType === 'date') {
    formConfig.push(
      {
        type: 'label',
        label: selectedTransferReason?.detailLabel,
      },
      {
        type: 'input',
        name: 'timestampDetail',
        label: selectedTransferReason?.detailLabel,
        input: 'date',
        trackingName: 'transfer-timestamp-detail-input',
      }
    );
  }

  if (selectedTransferReason?.detailType === 'string') {
    formConfig.push(
      {
        type: 'label',
        label: selectedTransferReason?.detailLabel,
      },
      {
        type: 'input',
        name: 'stringDetail',
        label: selectedTransferReason?.detailLabel,
        input: 'text',
        props: {
          trackingName: 'transfer-string-detail-input',
        },
      }
    );
  }

  if (selectedTransferReason?.detailType === 'address') {
    formConfig.push(
      {
        type: 'label',
        label: selectedTransferReason?.detailLabel,
      },
      {
        type: 'input',
        name: 'numericDetail',
        label: selectedTransferReason?.detailLabel,
        input: 'select',
        props: {
          options: addressOptions,
          trackingName: 'transfer-address-select',
        },
      }
    );
  }

  if (selectedTransferReason) {
    formConfig.push(
      {
        type: 'label',
        label: 'Which guide could best help this member?',
      },
      {
        type: 'passthrough',
        content: <ControlledGuideSelector control={control} errors={errors} />,
      },
      {
        type: 'label',
        label: 'Leave a comment to help the next guide learn about the member.',
      },
      {
        type: 'input',
        name: 'commentToReceivingGuide',
        label: 'Comments',
        input: 'text',
        props: {
          required: true,
          multiline: true,
          characterLimit: 500,
          trackingName: 'transfer-comment-input',
        },
      }
    );
  }

  return formConfig;
};
